import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useCurrencyFormatter } from '../../../../../../../hooks/useCurrencyFormatter';
import { useNamespacedTranslation } from '../../../../../../../hooks/useNamespacedTranslation';
import { frequencyOptions } from './types';

type SalaryComparisonBoxProps = {
  updateAmountOnly: boolean;
  hasValidInput: boolean;
  newValue: number;
  effectiveCurrentSalary: number;
  effectiveCurrentFrequency: string;
  difference: number;
  percentageChange: number;
  frequency?: string;
  currency: string;
};

export const SalaryComparisonBox: React.FC<SalaryComparisonBoxProps> = ({
  updateAmountOnly,
  hasValidInput,
  newValue,
  effectiveCurrentSalary,
  effectiveCurrentFrequency,
  difference,
  percentageChange,
  frequency,
  currency,
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm.salaryChange');
  const formatCurrency = useCurrencyFormatter();

  const isPositive = difference > 0;
  const isNegative = difference < 0;

  if (updateAmountOnly) {
    // Quick Update Mode: Always show 3-column layout
    return (
      <div className="mt-3 p-3 border rounded bg-light">
        <Row className="text-center">
          <Col md={4}>
            <div>
              <small className="text-muted d-block mb-1">
                {t('currentValue')}
              </small>
              <strong>
                {formatCurrency({ value: effectiveCurrentSalary, currency })}
              </strong>
              <small className="text-muted d-block mt-1">
                {t(
                  'options.' +
                    frequencyOptions.find(
                      (option) => option.value === effectiveCurrentFrequency,
                    )?.label,
                )}
              </small>
            </div>
          </Col>
          <Col md={4}>
            <div>
              <small className="text-muted d-block mb-1">{t('newValue')}</small>
              <strong>
                {hasValidInput
                  ? formatCurrency({ value: newValue, currency })
                  : '-'}
              </strong>
              <small className="text-muted d-block mt-1">
                {t(
                  'options.' +
                    frequencyOptions.find(
                      (option) => option.value === effectiveCurrentFrequency,
                    )?.label,
                )}
              </small>
            </div>
          </Col>
          <Col md={4}>
            <div>
              <small className="text-muted d-block mb-1">
                {t('difference')}
              </small>
              <div>
                {hasValidInput ? (
                  <>
                    <strong
                      className={
                        isPositive
                          ? 'text-success'
                          : isNegative
                          ? 'text-danger'
                          : 'text-muted'
                      }>
                      {isPositive ? '+' : ''}
                      {formatCurrency({
                        value: Math.abs(difference),
                        currency,
                      })}
                    </strong>
                    <br />
                    <small
                      className={
                        isPositive
                          ? 'text-success'
                          : isNegative
                          ? 'text-danger'
                          : 'text-muted'
                      }>
                      ({isPositive ? '+' : ''}
                      {percentageChange.toFixed(1)}%)
                    </small>
                  </>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  } else {
    // Advanced Mode: Show only new value in single column
    const displayFrequency = frequency || '-';
    return (
      <div className="mt-3 p-3 border rounded bg-light">
        <Row className="text-center">
          <Col md={12}>
            <div>
              <small className="text-muted d-block mb-1">{t('newValue')}</small>
              <strong>
                {hasValidInput
                  ? formatCurrency({ value: newValue, currency })
                  : '-'}
              </strong>
              <small className="text-muted d-block mt-1">
                {t(
                  'options.' +
                    frequencyOptions.find(
                      (option) => option.value === displayFrequency,
                    )?.label,
                )}
              </small>
            </div>
          </Col>
        </Row>
      </div>
    );
  }
};
